import {ReactNode} from 'react'
import cn from 'clsx'
import {differenceInCalendarDays, parseISO} from 'date-fns'
import {Space, Avatar} from 'antd'

import styles from './ProjectsList.module.scss'
import useStore from 'base/store'
import {
  ProjectType,
  PROJECT_CATEGORY_NAME,
  ProjectCategory,
} from 'modules/project'
import {getShortName} from 'modules/organisations'
import {useGetNotifications} from 'common/api'
import {
  Box,
  // IconButton,
  Block,
  ListHeader,
  Text,
  Empty,
  Loader,
  UserAvatar,
  Meter,
  AttachmentLinkIcon,
  MoneyBanknoteExchangeIcon,
  MoneyBanknotesRotatedSecondIcon,
  SortSecondIcon,
  // PinIcon, DownloadDotIcon, 
  FolderBookmarkIcon,
} from 'common/components'
import {
  formatDate,
  maybePluralize,
  formatMoney,
  // totalNumberFromArray,
  countPercentage,
  // createReport, capitalizeFirstLetter,
} from 'common/helpers'
import {SetSort} from 'common/types'
// import {
//   uniqueUsersFromProjects,
//   getProjectsReportData,
//   useGetTotalXirr,
// } from '..'

interface ProjectsListProps {
  isRefetching?: boolean;
  isManager?: boolean
  type: ProjectType
  sort: string
  items?: any[]
  setSort: SetSort
}

interface ProjectProps {
  notManager: boolean
  project: any
  projectsNotificationsUuids: string[]
}

interface HeadProps {
  type: ProjectType
  notManager: boolean
  sort: string
  setSort: SetSort
}

// interface BarProps {
//   type: ProjectType
//   projects: any[]
// }

interface SortProps {
  name: string
  title: ReactNode
  sort: string
  setSort: SetSort
  hide?: boolean;
}

export default function ProjectsList({
  isRefetching,
  type,
  sort,
  items,
  setSort,
}: ProjectsListProps) {
  const role = useStore((state) => state.role)
  const {data: notifications} = useGetNotifications()

  if (!items) return <Loader />

  if (items.length === 0)
    return (
      <Empty
        icon={<FolderBookmarkIcon width={40} height="100%" />}
        title="projects"
      />
    )

  let sortedItems = items.slice()

  const [sortOrder, sortName] = sort.split(' ')

  if (sortName === 'title') {
    sortedItems.sort((a, b) => {
      const first = a.title.toLowerCase()
      const second = b.title.toLowerCase()
      if (first < second) {
        return sortOrder === 'desc' ? -1 : 1
      }
      if (first > second) {
        return sortOrder === 'desc' ? 1 : -1
      }
      return 0
    })
  } else if (sortName === 'maturity') {
    sortedItems.sort((a, b) => {
      const first = +parseISO(a.maturityDate)
      const second = +parseISO(b.maturityDate)
      return sortOrder === 'desc' ? second - first : first - second
    })
  } else if (sortName === 'irr') {
    sortedItems.sort((a, b) => {
      const first = a.xirr
      const second = b.xirr
      return sortOrder === 'desc' ? second - first : first - second
    })
  } else if (sortName === 'income') {
    sortedItems.sort((a, b) => {
      const first = a.currentIncome
      const second = b.currentIncome
      return sortOrder === 'desc' ? second - first : first - second
    })
  } else if (sortName === 'undrawn') {
    sortedItems.sort((a, b) => {
      const first = a.availableWithdrawal
      const second = b.availableWithdrawal
      return sortOrder === 'desc' ? second - first : first - second
    })
  } else if (sortName === 'balance') {
    sortedItems.sort((a, b) => {
      const first = a.balance
      const second = b.balance
      return sortOrder === 'desc' ? second - first : first - second
    })
  } else if (sortName === 'facilityLimit2') {
    sortedItems.sort((a, b) => {
      const first = a.facilityLimit2
      const second = b.facilityLimit2
      return sortOrder === 'desc' ? second - first : first - second
    })
  }

  const projectsNotificationsUuids = notifications ? notifications.map(
    (notification: any) => notification.data?.project.uuid
  ) : []

  return (
    <>
      <Head
        type={type}
        notManager={!role.manager}
        sort={sort}
        setSort={setSort}
      />
      { !!isRefetching ?
        <Loader />
        :
        <ul className={styles.list}>
          {sortedItems.map((item) => (
            <Project
              key={item.uuid}
              notManager={!role.manager}
              project={item}
              projectsNotificationsUuids={projectsNotificationsUuids}
            />
          ))}
        </ul>
      }
      {/* <Bar type={type} projects={items} /> */}
    </>
  )
}

const Head = ({type, notManager, ...sort}: HeadProps) => (
  <ListHeader className={cn(styles.grid, styles.grid_header)}>
    <div>
      <Sort
        {...sort}
        hide
        name="title"
        title={
          <>
            Project
            <Dot />
            type
          </>
        }
      />
    </div>
    <div>
      Borrower
      {notManager && (
        <>
          <Dot />
          LM
        </>
      )}
    </div>
    <Box align="center">
      <Sort {...sort} name="maturity" title="Maturity" />
    </Box>
    <div>
      <Sort {...sort} name="irr" title="IRR forecast" hide />
    </div>
    <Box align="center">
      <Sort {...sort} name="income" title="Income" hide />
    </Box>
    <Box align="center">
      <Sort {...sort} name="undrawn" title="Undrawn" hide />
    </Box>
    <div>
      <Sort {...sort} name="balance" title="Loan balance" />
    </div>
    <Box align="right" className={styles.facilityLimit2}>
      <Sort {...sort} name="facilityLimit2" title="Facility limit 2" />
    </Box>
    <Box align="center">UPD</Box>
  </ListHeader>
)

const Sort = ({hide, name, sort, title, setSort}: SortProps) => {
  const [order, prop] = sort.split(' ')
  const isSort = sort && name === prop
  const isAsc = isSort && order === 'asc'

  const handleSort = () => {
    if (!isSort) return setSort(`desc ${name}`)
    if (isAsc) return setSort(`desc ${name}`)
    setSort(`asc ${name}`)
  }

  return (
    <button
      className={cn(
        styles.sort,
        isSort && styles.sort_active,
        isAsc && styles.sort_asc
      )}
      onClick={!hide ? handleSort:undefined}
    >
      { !hide &&
        <SortSecondIcon />
      }
      <span>{title}</span>
    </button>
  )
}

const Project = ({
  notManager,
  project,
  projectsNotificationsUuids,
}: ProjectProps) => {
  const {borrower, manager} = project
  const isClosed = project.status === 'closed'
  const letfDays = differenceInCalendarDays(
    parseISO(project.maturityDate),
    new Date()
  )
  const isOverdue = !isClosed && letfDays < 0
  const balanceToFacilityLimitPercent = countPercentage(
    project.balance,
    project.facilityLimit1
  )
  const notificationsCount = projectsNotificationsUuids.filter(
    (uuid) => uuid === project.uuid
  ).length

  return (
    <Block
      as="li"
      className={cn(styles.grid, styles.item, isOverdue && styles.item_overdue)}
      type="grey"
      to={`/projects/${project.uuid}`}
    >
      <Space size={12}>
        <Space>
          <Avatar src={project.image} alt={project.title} />
          <div>
            <p className={styles.title}>{project.title}</p>
            <p className={cn(styles.label, styles.label_categories)}>
              {project.categories
                .map(
                  (category: ProjectCategory) => PROJECT_CATEGORY_NAME[category]
                )
                .join(' • ')}
            </p>
          </div>
        </Space>
      </Space>
      <Space>
        <Avatar.Group>
          {borrower && <UserAvatar size={24} src={borrower.photo} alt={borrower.firstName} />}
          {notManager && manager && (
            <UserAvatar size={24} src={manager.photo} alt={manager.firstName} />
          )}
        </Avatar.Group>
        <Text as="span" size="xs" color="black70">
          {getShortName(borrower)}
          {notManager && manager && (
            <>
              <br />
              <AttachmentLinkIcon /> {manager.firstName}
            </>
          )}
        </Text>
      </Space>
      <Box align="center" className={styles.date}>
        <Text weight="demiBold">
          {formatDate(project.maturityDate, 'fifth')}
        </Text>
        <p className={styles.label}>
          {maybePluralize(letfDays < 0 ? Math.abs(letfDays) : letfDays, 'day')}{' '}
          {isClosed ? 'ago' : isOverdue ? 'overdue' : 'left'}
        </p>
      </Box>
      <div>
        <Space size={4}>
          <MoneyBanknoteExchangeIcon className={styles.icon} />
          <span>{project.xirr ?? 0}%</span>
        </Space>
        <p className={styles.label}>
          {project.xirrFees ?? 0}%
          <Dot />
          {project.xirrInterest ?? 0}%
        </p>
      </div>
      <Text align="right">{formatMoney(project.currentIncome)}</Text>
      <Text align="right">{formatMoney(project.availableWithdrawal)}</Text>
      <div className={styles.balance}>
        <Space size={4}>
          <MoneyBanknotesRotatedSecondIcon
            className={cn(styles.icon, styles.icon_green)}
          />
          <Text as="span" weight="medium" size="lg">
            {formatMoney(project.balance)}
          </Text>
        </Space>
        {Boolean(balanceToFacilityLimitPercent) && (
          <div className={styles.meter}>
            <Text size="2xs" color="grey" mb={2}>
              {balanceToFacilityLimitPercent}%
            </Text>
            <Meter
              size="small"
              width={48}
              value={balanceToFacilityLimitPercent}
            />
          </div>
        )}
      </div>
      <Text align="right" className={styles.facilityLimit2}>{formatMoney(project.facilityLimit2)}</Text>
      <Box align="right">
        {notificationsCount ? (
          <div className={styles.count}>{notificationsCount}</div>
        ) : null}
      </Box>
    </Block>
  )
}
/*
const Bar = ({type, projects}: BarProps) => {
  const [isSticky, setSticky] = useState(false)
  const {data: totalXirr} = useGetTotalXirr()

  const handleReport = () =>
    createReport(
      getProjectsReportData(projects),
      `${capitalizeFirstLetter(type)} filtered projects`,
      `${type}-filtered-projects-report`
    )

  const handleStickyToggle = () => setSticky((state) => !state)

  const borrowers = uniqueUsersFromProjects(projects, 'borrower')
  const totalIncome = totalNumberFromArray(projects, 'currentIncome')
  const totalUndrawn = totalNumberFromArray(projects, 'availableWithdrawal')
  const totalFacilityLimit = totalNumberFromArray(projects, 'facilityLimit2')
  const totalLoanBalance = totalNumberFromArray(projects, 'balance')

  return (
    <div className={cn(styles.bar, isSticky && styles.bar_sticky)}>
      <IconButton
        className={styles.pin}
        icon={<PinIcon />}
        onClick={handleStickyToggle}
      />
      <Block className={cn(styles.grid, styles.grid_bar)} type="black">
        <div>
          <Text size="xs" color="white50" mb={4}>
            number of projects
          </Text>
          <p className={styles.barValue}>
            {maybePluralize(
              projects.length,
              `${type === 'closed' ? 'closed ' : ''}project`
            )}
          </p>
        </div>
        <div>
          <Text size="xs" color="white50" mb={4}>
            borrowers
          </Text>
          <Space>
            <Avatar.Group maxCount={8} size={24}>
              {borrowers.map((borrower) => (
                <UserAvatar
                  key={borrower.uuid}
                  size={24}
                  src={borrower.photo}
                  alt={borrower.firstName}
                />
              ))}
            </Avatar.Group>
            <Text as="span" weight="demiBold">
              {maybePluralize(borrowers.length, 'user')}
            </Text>
          </Space>
        </div>
        <div>
          <Text size="xs" color="white50" mb={4}>
            total IRR
          </Text>
          <Space size={6}>
            <MoneyBanknoteExchangeIcon
              className={cn(styles.icon, styles.icon_atmo)}
            />
            <span className={styles.barValue}>
              {isFinite(totalXirr) ? `${totalXirr}%` : '...'}
            </span>
          </Space>
        </div>
        <Box align="center">
          <Text size="xs" color="white50" mb={4}>
            total income
          </Text>
          <Text weight="medium">{formatMoney(totalIncome)}</Text>
        </Box>
        <Box align="center">
          <Text size="xs" color="white50" mb={4}>
            total undrawn
          </Text>
          <Text weight="medium">{formatMoney(totalUndrawn)}</Text>
        </Box>
        <div>
          <Text className={styles.doubleLabel}>
            <span>total balance</span>
            <span className={styles.spacer} />
            <span>total facility limit 2</span>
          </Text>
          <div className={styles.total}>
            <Space size={6}>
              <MoneyBanknotesRotatedSecondIcon
                className={cn(styles.icon, styles.icon_green)}
              />
              <span className={styles.barValue}>
                {formatMoney(totalLoanBalance)}
              </span>
            </Space>
            <p className={cn(styles.barValue, styles.barValue_dimm)}>
              {formatMoney(totalFacilityLimit)}
            </p>
          </div>
        </div>
        <div className={styles.downloadBlock}>
          <IconButton
            className={styles.download}
            icon={<DownloadDotIcon />}
            onClick={handleReport}
          />
        </div>
      </Block>
    </div>
  )
}
*/
const Dot = () => (
  <Box as="span" ml={4} mr={4}>
    •
  </Box>
)
